import { useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import get from "lodash/get";
import { XIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { FormInput } from "components/BaseForm";
import { REQUIRED_MARK } from "components/BaseForm/fields/fieldClasses";
import { priceAmountError } from "routes/product/pricing/pricing";

const USED_EDIT_MESSAGE =
  "This price is used on existing invoice lines and can't be edited.";
const USED_DELETE_MESSAGE =
  "This price is used on existing invoice lines and can't be deleted.";

const deleteBlockedReason = price => {
  if (price.is_used) return USED_DELETE_MESSAGE;
  if (price.is_current) {
    return "The current price can't be deleted. Make another price current first.";
  }
  return undefined;
};

const PriceVersionsField = () => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: "prices" });
  const prices = useWatch({ name: "prices" }) ?? [];
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState(null);
  const listError = get(errors, "prices")?.message;

  const addPrice = () => {
    const message = priceAmountError(draft, prices);
    setDraftError(message);
    if (message) return;
    // The first price becomes current; later ones wait, disabled, until the
    // user enables them or makes one current.
    const isFirst = prices.length === 0;
    append({
      amount: draft.trim(),
      is_current: isFirst,
      enabled: isFirst,
      is_used: false,
    });
    setDraft("");
  };

  const makeCurrent = index => {
    prices.forEach((price, i) => {
      if (price.is_current) {
        setValue(`prices.${i}.is_current`, false, { shouldDirty: true });
        setValue(`prices.${i}.enabled`, true, { shouldDirty: true });
      }
    });
    setValue(`prices.${index}.is_current`, true, { shouldDirty: true });
    setValue(`prices.${index}.enabled`, true, { shouldDirty: true });
  };

  return (
    <Field data-invalid={listError ? true : undefined}>
      <FieldLabel htmlFor="new-price" className={REQUIRED_MARK}>
        Price Versions
      </FieldLabel>
      {fields.map((field, index) => {
        const price = prices[index] ?? field;
        return (
          <div
            key={field.id}
            className="tw:flex tw:items-start tw:gap-2"
            data-testid="price-version"
          >
            <div className="tw:w-36">
              <FormInput
                name={`prices.${index}.amount`}
                aria-label={`Price ${index + 1} amount`}
                inputMode="decimal"
                prefix="$"
                disabled={price.is_used}
              />
            </div>
            <Badge
              variant={price.is_current ? "default" : "secondary"}
              className="tw:mt-2"
            >
              {price.is_current ? "Current" : "Historic"}
            </Badge>
            {!price.is_current && (
              <>
                <Switch
                  className="tw:mt-2"
                  aria-label={`Price ${index + 1} enabled`}
                  checked={price.enabled}
                  onCheckedChange={checked =>
                    setValue(`prices.${index}.enabled`, checked, {
                      shouldDirty: true,
                    })
                  }
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => makeCurrent(index)}
                >
                  Make Current
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Delete price ${index + 1}`}
              title={deleteBlockedReason(price)}
              disabled={price.is_current || price.is_used}
              onClick={() => remove(index)}
            >
              <XIcon />
            </Button>
            {price.is_used && (
              <span className="tw:mt-2 tw:text-xs tw:text-muted-foreground">
                {USED_EDIT_MESSAGE} {USED_DELETE_MESSAGE}
              </span>
            )}
          </div>
        );
      })}
      <div className="tw:flex tw:items-center tw:gap-2">
        <Input
          id="new-price"
          className="tw:w-36"
          inputMode="decimal"
          placeholder="0.00"
          value={draft}
          onChange={event => setDraft(event.target.value)}
          aria-invalid={draftError ? true : undefined}
        />
        <Button type="button" variant="outline" size="sm" onClick={addPrice}>
          Add Price
        </Button>
      </div>
      <FieldError>{draftError ?? listError}</FieldError>
    </Field>
  );
};

export default PriceVersionsField;
